/**
 * Renders an ingredient declaration as safe HTML with allergen words in bold.
 *
 * The catalogue ships ingredient text using MS-Access allergen markers of the
 * form `<ALLERGEN#` (e.g. `<WHEAT#`, `(<MILK#)`). Injected verbatim into the
 * DOM the browser treats `<ALMOND#, ...` as an unclosed tag and swallows the
 * rest of the line — so we must transform the markers into real markup.
 *
 * If the backend ever ships pre-bolded HTML (`<b>`/`<strong>` or HTML
 * entities) we trust and render it as-is (mirrors the PWA's boldAllergens).
 */

const ALLERGEN_KEYWORDS = new Set([
  'WHEAT', 'MILK', 'EGG', 'EGGS', 'ALMOND', 'ALMONDS', 'HAZELNUT', 'HAZELNUTS',
  'SOYA', 'GLUTEN', 'BARLEY', 'OAT', 'OATS', 'RYE', 'SPELT', 'KAMUT',
  'CASHEW', 'CASHEWS', 'PECAN', 'PECANS', 'WALNUT', 'WALNUTS',
  'PISTACHIO', 'PISTACHIOS', 'MACADAMIA', 'BRAZIL',
  'PEANUT', 'PEANUTS', 'FISH', 'CRUSTACEAN', 'CRUSTACEANS',
  'MOLLUSC', 'MOLLUSCS', 'CELERY', 'MUSTARD', 'SESAME',
  'SULPHITE', 'SULPHITES', 'SULPHUR', 'LUPIN', 'LUPINS',
  'SHELLFISH', 'NUT', 'NUTS',
]);

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function formatIngredients(text?: string | null): string {
  if (!text) return '';

  // Backend already shipped safe pre-bolded HTML — render verbatim.
  if (/<\/?(?:b|strong)>/i.test(text) || /&(?:amp|lt|gt);/.test(text)) {
    return text;
  }

  // Raw `<ALLERGEN#` markers. Escape everything first (XSS-safe), then strip
  // the markers down to the bare keyword, then bold recognised allergens in a
  // single pass (mirrors the PWA — no double-wrapping).
  let html = escapeHtml(text);
  html = html.replace(/&lt;([A-Za-z]{2,})#/g, '$1');
  // Clean up any stray, malformed marker fragments.
  html = html.replace(/&lt;([A-Za-z])/g, '$1').replace(/([A-Za-z])#/g, '$1');
  html = html.replace(/\b([A-Z]{2,})\b/g, (match) =>
    ALLERGEN_KEYWORDS.has(match) ? `<b>${match}</b>` : match,
  );
  return html;
}
